import React, { useState, useEffect } from 'react';
import {
  Users, UserPlus, Mail, Phone, MapPin, Calendar,
  Edit2, Trash2, Eye, MoreVertical, Search, Filter,
  ChevronDown, Download, RefreshCw, CheckCircle,
  XCircle, Shield, Package, IndianRupee, Star,
  UserCheck, UserX, TrendingUp, ShoppingBag, Loader2
} from 'lucide-react';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedRole, setSelectedRole] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    newToday: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1
  });

  const API_BASE = 'https://api.maechi.in';

  // Fetch customers
  const fetchCustomers = async (page = pagination.page) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page,
        limit: pagination.limit,
        ...(searchQuery && { search: searchQuery }),
        ...(selectedStatus !== 'all' && { status: selectedStatus }),
        ...(selectedRole !== 'all' && { role: selectedRole }),
        sort: 'created_at',
        order: 'DESC'
      });

      const response = await fetch(`${API_BASE}/get-customers.php?${params}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setCustomers(data.data || []);
        setPagination(data.pagination || pagination);
        setStats(data.stats || stats);
      } else {
        setError(data.message || 'Failed to fetch customers');
      }
    } catch (err) {
      console.error('API Error:', err);
      setError(err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Debounce search
    const timeoutId = setTimeout(() => {
      fetchCustomers(1); // Reset to page 1 on filter change
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [searchQuery, selectedStatus, selectedRole]);

  // Initial load
  useEffect(() => {
    fetchCustomers();
  }, [pagination.page]);

  // Handle delete customer
  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete ${name}? This action cannot be undone.`)) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/get-customers.php`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage(`Customer "${name}" deleted successfully`);
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchCustomers(); // Refresh data
      } else {
        setError(data.message || 'Failed to delete customer');
      }
    } catch (err) {
      console.error('Delete Error:', err);
      setError('Failed to delete customer');
    } finally {
      setLoading(false);
    }
  };

  // Handle status change
  const handleStatusChange = async (id, newStatus, name) => {
    try {
      const response = await fetch(`${API_BASE}/get-customers.php`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          id: id,
          status: newStatus 
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setCustomers(customers.map(c => 
          c.id === id ? { ...c, status: newStatus } : c
        ));
        setSuccessMessage(`Customer "${name}" ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchCustomers(); // Refresh stats
      } else {
        setError(data.message || 'Failed to update status');
      }
    } catch (err) {
      console.error('Status Update Error:', err);
      setError('Failed to update status');
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    }
    
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Get default address
  const getDefaultAddress = (customer) => {
    const addresses = customer.addresses || [];
    const defaultAddr = addresses.find(addr => addr.is_default === '1' || addr.is_default === 1);
    return defaultAddr || addresses[0] || null;
  };

  // Loading state
  if (loading && customers.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading customers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Users className="mr-3" size={28} />
            Customers
          </h1>
          <p className="text-gray-600 mt-1">Manage your customer accounts and details</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => fetchCustomers()}
            disabled={loading}
            className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw size={18} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center">
            <Download size={18} className="mr-2" />
            Export
          </button>
          <button className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center transition-colors">
            <UserPlus size={18} className="mr-2" />
            Add Customer
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="p-3 bg-blue-50 rounded-lg mr-4">
              <Users className="text-blue-600" size={24} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Total Customers</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="p-3 bg-green-50 rounded-lg mr-4">
              <UserCheck className="text-green-600" size={24} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Active Customers</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="p-3 bg-red-50 rounded-lg mr-4">
              <UserX className="text-red-600" size={24} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Inactive Customers</p>
              <p className="text-2xl font-bold text-gray-900">{stats.inactive}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="p-3 bg-purple-50 rounded-lg mr-4">
              <TrendingUp className="text-purple-600" size={24} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">New Today</p>
              <p className="text-2xl font-bold text-gray-900">{stats.newToday}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white p-4 rounded-xl border border-gray-200">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="relative w-full lg:w-auto lg:flex-1 max-w-2xl">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search customers by name, email, phone..."
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
            <select
              className="px-4 py-2.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            
            <select
              className="px-4 py-2.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              <option value="all">All Roles</option>
              <option value="customer">Customer</option>
              <option value="admin">Admin</option>
              <option value="vendor">Vendor</option>
            </select>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center animate-in slide-in-from-top">
          <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
          <span className="text-green-800">{successMessage}</span>
          <button 
            onClick={() => setSuccessMessage('')} 
            className="ml-auto hover:bg-green-100 p-1 rounded-full transition-colors"
          >
            <XCircle className="w-4 h-4 text-green-600" />
          </button>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center animate-in slide-in-from-top">
          <XCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0" />
          <span className="text-red-800">{error}</span>
          <button 
            onClick={() => setError('')} 
            className="ml-auto hover:bg-red-100 p-1 rounded-full transition-colors"
          >
            <XCircle className="w-4 h-4 text-red-600" />
          </button>
        </div>
      )}

      {/* Customers Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-4 text-sm font-semibold text-gray-700">Customer</th>
                <th className="text-left p-4 text-sm font-semibold text-gray-700">Contact</th>
                <th className="text-left p-4 text-sm font-semibold text-gray-700">Orders</th>
                <th className="text-left p-4 text-sm font-semibold text-gray-700">Total Spent</th>
                <th className="text-left p-4 text-sm font-semibold text-gray-700">Status</th>
                <th className="text-left p-4 text-sm font-semibold text-gray-700">Last Login</th>
                <th className="text-left p-4 text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="p-8 text-center">
                    <Users className="mx-auto text-gray-300" size={48} />
                    <h3 className="text-lg font-medium text-gray-900 mt-4">No customers found</h3>
                    <p className="text-gray-600 mt-2">Try changing your search or filter criteria</p>
                  </td>
                </tr>
              ) : (
                customers.map((customer) => {
                  const defaultAddress = getDefaultAddress(customer);
                  return (
                    <tr key={customer.id} className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="p-4">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                            <span className="text-blue-600 font-semibold">
                              {customer.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{customer.name}</p>
                            <div className="flex items-center mt-1 flex-wrap gap-1">
                              {customer.role === 'admin' && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                  <Shield size={10} className="mr-1" />
                                  Admin
                                </span>
                              )}
                              {customer.role === 'vendor' && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                                  <Package size={10} className="mr-1" />
                                  Vendor
                                </span>
                              )}
                              <span className="text-xs text-gray-500">
                                Joined {formatDate(customer.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="space-y-1">
                          <div className="flex items-center text-gray-700">
                            <Mail size={14} className="mr-2 text-gray-400 flex-shrink-0" />
                            <span className="text-sm truncate">{customer.email}</span>
                          </div>
                          <div className="flex items-center text-gray-700">
                            <Phone size={14} className="mr-2 text-gray-400 flex-shrink-0" />
                            <span className="text-sm">{customer.phone}</span>
                          </div>
                          {defaultAddress && (
                            <div className="flex items-center text-gray-700">
                              <MapPin size={14} className="mr-2 text-gray-400 flex-shrink-0" />
                              <span className="text-sm truncate">
                                {defaultAddress.city}, {defaultAddress.state}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center">
                          <ShoppingBag size={18} className="text-gray-400 mr-2 flex-shrink-0" />
                          <div>
                            <span className="font-medium text-gray-900">{customer.order_count}</span>
                            <span className="text-gray-500 text-sm ml-1">orders</span>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center">
                          <IndianRupee size={16} className="text-gray-400 mr-1 flex-shrink-0" />
                          <span className="font-bold text-gray-900">
                            {formatCurrency(customer.total_spent)}
                          </span>
                        </div>
                        {parseFloat(customer.total_spent) > 10000 && (
                          <span className="inline-flex items-center text-xs text-yellow-600 mt-1">
                            <Star size={10} className="mr-1" fill="currentColor" />
                            VIP Customer
                          </span>
                        )}
                      </td>
                      <td className="p-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium inline-flex items-center ${
                          customer.status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {customer.status === 'active' ? (
                            <>
                              <div className="w-2 h-2 rounded-full bg-green-600 mr-2"></div>
                              Active
                            </>
                          ) : (
                            <>
                              <div className="w-2 h-2 rounded-full bg-red-600 mr-2"></div>
                              Inactive
                            </>
                          )}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center text-gray-700">
                          <Calendar size={14} className="mr-2 text-gray-400 flex-shrink-0" />
                          <span className="text-sm">
                            {customer.last_login ? formatDate(customer.last_login) : 'Never'}
                          </span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-1">
                          <button 
                            onClick={() => {/* View customer details */}}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye size={18} />
                          </button>
                          <button 
                            onClick={() => {/* Edit customer */}}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button 
                            onClick={() => handleStatusChange(
                              customer.id, 
                              customer.status === 'active' ? 'inactive' : 'active',
                              customer.name
                            )}
                            className={`p-2 rounded-lg transition-colors ${
                              customer.status === 'active'
                                ? 'text-red-600 hover:bg-red-50'
                                : 'text-green-600 hover:bg-green-50'
                            }`}
                            title={customer.status === 'active' ? 'Deactivate' : 'Activate'}
                          >
                            {customer.status === 'active' ? <UserX size={18} /> : <UserCheck size={18} />}
                          </button>
                          <button 
                            onClick={() => handleDelete(customer.id, customer.name)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {customers.length > 0 && pagination.totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between bg-white p-4 rounded-xl border border-gray-200 gap-4">
          <div className="text-sm text-gray-600">
            Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
            {pagination.total} customers
          </div>
          <div className="flex items-center space-x-2">
            <button 
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1 || loading}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              Previous
            </button>
            
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                let pageNum;
                if (pagination.totalPages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.page <= 3) {
                  pageNum = i + 1;
                } else if (pagination.page >= pagination.totalPages - 2) {
                  pageNum = pagination.totalPages - 4 + i;
                } else {
                  pageNum = pagination.page - 2 + i;
                }
                
                if (pageNum > 0 && pageNum <= pagination.totalPages) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-10 h-10 rounded-lg text-sm ${
                        pagination.page === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 hover:bg-gray-50 text-gray-700'
                      } transition-colors`}
                      disabled={loading}
                    >
                      {pageNum}
                    </button>
                  );
                }
                return null;
              })}
            </div>
            
            <button 
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages || loading}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;